import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { addMoney, Banner, Button, Card, compareMoney, formatMoney, isZero, Money, negate, PageHeader, Pill, Tabs } from '@elysian/ui';
import type { AccountBalance, AgedBalance } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { summarisePnl } from '../Overview';
import { BookExports } from './BookExports';
import { iso, longDate } from '../invoices/shared';

type Report = 'pnl' | 'balance' | 'trial' | 'debtors' | 'creditors';
const TITLES: Record<Report, string> = {
  pnl: 'Profit and loss', balance: 'Balance sheet', trial: 'Trial balance', debtors: 'Aged debtors', creditors: 'Aged creditors',
};

/** Rows to CSV, quoting as spreadsheets expect. */
export function toCsv(rows: (string | number | null | undefined)[][]): string {
  return rows.map((r) => r.map((v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\r\n') + '\r\n';
}

const plain = (v: string) => v.replace(/(\.\d\d)\d*$/, '$1');

export function Reports() {
  const { today } = useSession();
  const [params, setParams] = useSearchParams();
  const report = (Object.keys(TITLES) as Report[]).includes(params.get('r') as Report) ? params.get('r') as Report : 'pnl';
  const now = iso(today());
  const [from, setFrom] = useState(`${now.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(now);
  const [asAt, setAsAt] = useState(now);
  const ranged = report === 'pnl';

  return (
    <>
      <PageHeader title="Reports" subtitle={ranged ? `${longDate(from)} to ${longDate(to)}` : `As at ${longDate(asAt)}`}
        actions={
          <div className="el-row">
            {ranged ? (
              <>
                <input type="date" className="el-input" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 'auto' }} />
                <input type="date" className="el-input" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 'auto' }} />
              </>
            ) : <input type="date" className="el-input" aria-label="As at" value={asAt} onChange={(e) => setAsAt(e.target.value)} style={{ width: 'auto' }} />}
          </div>
        } />
      <Card>
        <Tabs<Report> label="Report" value={report} onChange={(r) => setParams({ r })}
          tabs={(Object.keys(TITLES) as Report[]).map((k) => ({ key: k, label: TITLES[k] }))} />
        <div style={{ padding: '4px 0 8px' }}>
          {report === 'pnl' && <ProfitAndLoss from={from} to={to} />}
          {report === 'balance' && <BalanceSheet asAt={asAt} />}
          {report === 'trial' && <TrialBalance asAt={asAt} />}
          {report === 'debtors' && <Aged kind="debtors" asAt={asAt} />}
          {report === 'creditors' && <Aged kind="creditors" asAt={asAt} />}
        </div>
      </Card>
      <div style={{ marginTop: 20 }}><BookExports /></div>
    </>
  );
}

function useDownload() {
  const toast = useToast((s) => s.show);
  return (name: string, rows: (string | number | null | undefined)[][]) => {
    try {
      const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast('Downloads aren’t available here.');
    }
  };
}

function Toolbar({ children, onCsv }: { children?: ReactNode; onCsv: () => void }) {
  return (
    <div className="el-spread" style={{ padding: '12px 20px' }}>
      <div className="el-row">{children}</div>
      <Button size="sm" onClick={onCsv}>Download CSV</Button>
    </div>
  );
}

function Loading<T>({ state, children }: { state: { state: 'loading' } | { state: 'error'; error: Error } | { state: 'ready'; data: T }; children: (d: T) => ReactNode }) {
  if (state.state === 'loading') return <div className="el-skeleton" style={{ height: 160, margin: 20 }} aria-label="Loading report" />;
  if (state.state === 'error') return <div style={{ padding: 20 }}><Banner>Couldn’t run the report: {state.error.message}</Banner></div>;
  return <>{children(state.data)}</>;
}

function Line({ label, code, value, strong, indent }: { label: ReactNode; code?: string | null; value: string; strong?: boolean; indent?: boolean }) {
  return (
    <tr className={strong ? 'el-report-total' : undefined}>
      <td style={indent ? { paddingLeft: 32 } : undefined}>{code && <span className="el-report-code">{code}</span>}{label}</td>
      <td className="el-num"><Money value={value} /></td>
    </tr>
  );
}

function ProfitAndLoss({ from, to }: { from: string; to: string }) {
  const { source } = useSession();
  const download = useDownload();
  const data = useAsync(() => source.profitAndLoss(from, to), [source, from, to], { keep: true });
  return (
    <Loading state={data}>{(lines) => {
      const s = summarisePnl(lines);
      const group = (f: (l: AccountBalance) => boolean) => lines.filter(f).sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));
      const income = group((l) => l.class === 'income');
      const cos = group((l) => l.class === 'expense' && (l.code ?? '').startsWith('5'));
      const over = group((l) => l.class === 'expense' && !(l.code ?? '').startsWith('5'));
      const margin = compareMoney(s.income, '0') > 0 ? Math.round((Number(s.gross) / Number(s.income)) * 1000) / 10 : null;
      return (
        <>
          <Toolbar onCsv={() => download(`profit-and-loss-${from}-${to}.csv`, [['Code', 'Account', 'Amount'], ...lines.map((l) => [l.code, l.name, plain(l.balance)]),
            ['', 'Gross profit', plain(s.gross)], ['', 'Net profit', plain(s.net)]])}>
            <Pill tone={compareMoney(s.net, '0') >= 0 ? 'ok' : 'danger'}>{compareMoney(s.net, '0') >= 0 ? 'Profit' : 'Loss'} {formatMoney(s.net.replace('-', ''))}</Pill>
            {margin !== null && <span className="el-hint">Gross margin {margin}%</span>}
          </Toolbar>
          {lines.length === 0 && <p className="el-muted" style={{ margin: '0 20px 12px' }}>Nothing has been posted between these dates yet. Invoices, bills and bank lines appear here once they’re posted.</p>}
          <div className="el-table-wrap">
            <table className="el-table el-report" aria-label="Profit and loss">
              <tbody>
                <tr className="el-report-head"><td colSpan={2}>Income</td></tr>
                {income.map((l) => <Line key={l.code} code={l.code} label={l.name} value={l.balance} indent />)}
                <Line label="Total income" value={s.income} strong />
                <tr className="el-report-head"><td colSpan={2}>Cost of sales</td></tr>
                {cos.map((l) => <Line key={l.code} code={l.code} label={l.name} value={l.balance} indent />)}
                <Line label="Gross profit" value={s.gross} strong />
                <tr className="el-report-head"><td colSpan={2}>Overheads</td></tr>
                {over.map((l) => <Line key={l.code} code={l.code} label={l.name} value={l.balance} indent />)}
                <Line label="Total overheads" value={s.overheads} strong />
                <tr className="el-report-grand"><td>Net profit</td><td className="el-num"><Money value={s.net} /></td></tr>
              </tbody>
            </table>
          </div>
        </>
      );
    }}</Loading>
  );
}

function BalanceSheet({ asAt }: { asAt: string }) {
  const { source } = useSession();
  const download = useDownload();
  const data = useAsync(() => source.balanceSheet(asAt), [source, asAt], { keep: true });
  return (
    <Loading state={data}>{(lines) => {
      const sum = (sec: string) => addMoney('0', ...lines.filter((l) => l.section === sec).map((l) => l.balance));
      const assets = sum('assets'), liabilities = sum('liabilities'), equity = sum('equity');
      const net = addMoney(assets, negate(liabilities));
      const balanced = compareMoney(net, equity) === 0;
      const section = (sec: string, title: string, total: string) => (
        <>
          <tr className="el-report-head"><td colSpan={2}>{title}</td></tr>
          {lines.filter((l) => l.section === sec).map((l) => <Line key={`${sec}${l.code ?? l.name}`} code={l.code} label={l.name} value={l.balance} indent />)}
          <Line label={`Total ${title.toLowerCase()}`} value={total} strong />
        </>
      );
      return (
        <>
          <Toolbar onCsv={() => download(`balance-sheet-${asAt}.csv`, [['Section', 'Code', 'Account', 'Amount'], ...lines.map((l) => [l.section, l.code, l.name, plain(l.balance)])])}>
            <Pill tone={balanced ? 'ok' : 'danger'}>{balanced ? 'Balances' : `Out by ${formatMoney(addMoney(net, negate(equity)))}`}</Pill>
          </Toolbar>
          {lines.length === 0 && <p className="el-muted" style={{ margin: '0 20px 12px' }}>Nothing has been posted up to this date yet.</p>}
          <div className="el-table-wrap">
            <table className="el-table el-report" aria-label="Balance sheet">
              <tbody>
                {section('assets', 'Assets', assets)}
                {section('liabilities', 'Liabilities', liabilities)}
                <tr className="el-report-grand"><td>Net assets</td><td className="el-num"><Money value={net} /></td></tr>
                {section('equity', 'Capital and reserves', equity)}
              </tbody>
            </table>
          </div>
        </>
      );
    }}</Loading>
  );
}

function TrialBalance({ asAt }: { asAt: string }) {
  const { source } = useSession();
  const download = useDownload();
  const data = useAsync(() => source.trialBalance(asAt), [source, asAt], { keep: true });
  return (
    <Loading state={data}>{(lines) => {
      const dr = addMoney('0', ...lines.map((l) => l.debit ?? '0'));
      const cr = addMoney('0', ...lines.map((l) => l.credit ?? '0'));
      const ok = compareMoney(dr, cr) === 0;
      return (
        <>
          <Toolbar onCsv={() => download(`trial-balance-${asAt}.csv`, [['Code', 'Account', 'Debit', 'Credit'], ...lines.map((l) => [l.code, l.name, plain(l.debit ?? '0'), plain(l.credit ?? '0')]), ['', 'Total', plain(dr), plain(cr)]])}>
            <Pill tone={ok ? 'ok' : 'danger'}>{ok ? 'Debits equal credits' : `Out by ${formatMoney(addMoney(dr, negate(cr)))}`}</Pill>
          </Toolbar>
          {lines.length === 0 && <p className="el-muted" style={{ margin: '0 20px 12px' }}>No entries up to this date yet. Opening balances, invoices, bills and journals all show here once posted.</p>}
          <div className="el-table-wrap">
            <table className="el-table el-report" aria-label="Trial balance">
              <thead><tr><th scope="col">Account</th><th scope="col" className="el-num">Debit</th><th scope="col" className="el-num">Credit</th></tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.code ?? l.name}>
                    <td><span className="el-report-code">{l.code}</span>{l.name}</td>
                    <td className="el-num">{l.debit && !isZero(l.debit) ? <Money value={l.debit} /> : ''}</td>
                    <td className="el-num">{l.credit && !isZero(l.credit) ? <Money value={l.credit} /> : ''}</td>
                  </tr>
                ))}
                <tr className="el-report-grand"><td>Total</td><td className="el-num"><Money value={dr} /></td><td className="el-num"><Money value={cr} /></td></tr>
              </tbody>
            </table>
          </div>
        </>
      );
    }}</Loading>
  );
}

const BUCKETS: { key: keyof AgedBalance; label: string }[] = [
  { key: 'current', label: 'Not due' }, { key: 'd30', label: '1–30' }, { key: 'd60', label: '31–60' },
  { key: 'd90', label: '61–90' }, { key: 'older', label: 'Over 90' }, { key: 'total', label: 'Total' },
];

function Aged({ kind, asAt }: { kind: 'debtors' | 'creditors'; asAt: string }) {
  const { source } = useSession();
  const download = useDownload();
  const data = useAsync(() => (kind === 'debtors' ? source.agedDebtors(asAt) : source.agedCreditors(asAt)), [source, kind, asAt], { keep: true });
  const base = kind === 'debtors' ? '/customers' : '/suppliers';
  return (
    <Loading state={data}>{(rows) => {
      const totals = Object.fromEntries(BUCKETS.map((b) => [b.key, addMoney('0', ...rows.map((r) => r[b.key] as string))]));
      const late = addMoney(totals.d30!, totals.d60!, totals.d90!, totals.older!);
      return (
        <>
          <Toolbar onCsv={() => download(`aged-${kind}-${asAt}.csv`, [[kind === 'debtors' ? 'Customer' : 'Supplier', ...BUCKETS.map((b) => b.label)],
            ...rows.map((r) => [r.contact_name, ...BUCKETS.map((b) => plain(r[b.key] as string))]), ['Total', ...BUCKETS.map((b) => plain(totals[b.key]!))]])}>
            <span className="el-hint">{rows.length} {kind === 'debtors' ? 'customers' : 'suppliers'} · {formatMoney(late)} overdue (days past the due date)</span>
          </Toolbar>
          <div className="el-table-wrap">
            <table className="el-table el-report" aria-label={TITLES[kind]} style={{ minWidth: 680 }}>
              <thead><tr><th scope="col">{kind === 'debtors' ? 'Customer' : 'Supplier'}</th>{BUCKETS.map((b) => <th key={b.key} scope="col" className="el-num">{b.label}</th>)}</tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.contact_id}>
                    <td><Link className="el-row-link" style={{ whiteSpace: 'normal' }} to={`${base}/${r.contact_id}`}>{r.contact_name}</Link></td>
                    {BUCKETS.map((b) => {
                      const v = r[b.key] as string;
                      const tone = b.key === 'd60' || b.key === 'd90' || b.key === 'older' ? 'danger' : b.key === 'd30' ? 'warn' : undefined;
                      return <td key={b.key} className="el-num">{isZero(v) ? '' : <Money value={v} tone={b.key === 'total' ? undefined : tone} />}</td>;
                    })}
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="el-muted">Nothing outstanding.</td></tr>}
                <tr className="el-report-grand"><td>Total</td>{BUCKETS.map((b) => <td key={b.key} className="el-num"><Money value={totals[b.key]!} /></td>)}</tr>
              </tbody>
            </table>
          </div>
        </>
      );
    }}</Loading>
  );
}
